import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSortAmountDown } from 'react-icons/fa';

import TableManager from '../components/data/TableManager';
import DragDropSequenceManager from '../common/DragDropSequenceManager';
import OrdinamentoCampiModal from '../components/Macrofunzioni/OrdinamentoCampiModal'; // AGGIUNTO: Modal ordinamento
import CampiOperatoriEditor from '../components/Macrofunzioni/CampiOperatoriEditor'; // NUOVO: Sub-editor SKE_CampiOperatori
import { apiService } from '../services/apiService';
import { logger } from '../utils/logger';
import { hubFields } from '../constants/miloFields';
import { isHubModule } from '../utils/moduli';

/**
 * Convenzione MILO: CAM_Sequenza ∈ [1, 1000] = campi DOMINIO (visibili nei template).
 *                   CAM_Sequenza > 1000      = campi TECNICI (audit, validità SCD2, ecc.)
 *                                              ignorati dai template via filtro numerico.
 *
 * I 6 campi CAM_* Milo sono visibili solo quando la tabella padre appartiene
 * al modulo il cui MOD_CodiceModulo e' HUB.
 */
// Configuration for SKE_Campi columns
const BASE_COLUMN_CONFIG = [
  // --- Keys and Identifiers ---
  "CAM_Id_Campi=0,ID Campo,isPrimaryKey",
  "CAM_IdRif_Campi=0,ID Rif. Campo,type=number",
  "CAM_Id_Tabelle=2,Tabella,required,lookupName=tabelleLookup", // <<<< CORRECTED FIELD NAME
  "CAM_Sequenza=2,Sequenza,required,type=number",
  "CAM_Nome=2,Nome Campo,required",
  "CAM_Descrizione=2,Descrizione",
  "CAM_Id_TipiCampi=2,Tipo Campo,lookupName=tipiCampiLookup", // FK, Visible in table & form // MODIFIED HERE
  "CAM_Lunghezza=1,Lunghezza", // Visible in table & form
  
  "CAM_Default=2,Default", // FIXED: Usa direttamente extraTabelleByProductLookup
  "CAM_NomeCampoChiaveEsterno=2,Nome Campo Chiave Esterna",
  "CAM_Extra_Tabelle=2,Tabella Esterna (FK),lookupName=extraTabelleByProductLookup,isBoolean",
  "CAM_ProgressivoOrdinamento=2,Progressivo Ordinamento,type=number", // Visibile nel form
  "CAM_TipoOrdinamento=0,Tipo Ordinamento,type=select", // Nascosto: gestito da Orderby default
  "CAM_ListaInFiltro=2,Tipo di ricerca in filtro,required,type=select",
  // --- Flags (Boolean-like integers, treat as booleans in UI) ---
  "CAM_Nullo=2,Nullo,type=boolean,isBoolean",
  "CAM_Incrementale=2,Incrementale,type=boolean,isBoolean",
  "CAM_ForeignKey=2,Foreign Key,type=boolean,isBoolean",
  "CAM_Like=2,Ricerca Like,type=boolean,isBoolean", // Form/detail only
  "CAM_AccettaZero=2,Accetta Zero,type=boolean,isBoolean", // Form/detail only
  "CAM_Scollegato=2,Scollegato,type=boolean,isBoolean", // Form/detail only

  // --- Foreign Key Details (Form/Detail only) ---
  "CAM_Suffisso=2,Suffisso",

  // --- Campi MILO/HUB (rimossi dalla configurazione nei contesti non-HUB) ---
  "CAM_ChiaveNaturale=2,Chiave Naturale,type=boolean,isBoolean",
  "CAM_Cercabile=2,Cercabile,type=boolean,isBoolean",
  "CAM_Versioned=2,Versioned (SCD2),type=boolean,isBoolean",
  "CAM_Cifrato=2,Cifrato,type=boolean,isBoolean",
  "CAM_NomeUi=2,Nome UI",
  "CAM_ErroreValidazione=2,Messaggio Errore Validazione",

  // --- Validity and Status (Hidden from table by default, visible in forms) ---
  "CAM_InizioValidita=0,Inizio Validità,type=datetime",
  "CAM_FineValidita=0,Fine Validità,type=datetime",
  "CAM_StatiRecord=0,Stato Record,",

  // --- Audit Fields (Hidden, managed by backend/trigger) ---
  "CAM_UtenteInserimento=0,Utente Inserimento",
  "CAM_TerminaleInserimento=0,Terminale Inserimento",
  "CAM_DataOraInserimento=0,Data/Ora Inserimento,type=datetime",
  "CAM_UtenteModifica=0,Utente Modifica",
  "CAM_TerminaleModifica=0,Terminale Modifica",
  "CAM_DataOraModifica=0,Data/Ora Modifica,type=datetime",
  "CAM_Timestamp=0,Timestamp DB,type=datetime",
];

// Configuration for Filters
const filterConfig = [
  { field: 'CAM_Nome', label: 'Nome Campo', type: 'text' },
  { field: 'CAM_Descrizione', label: 'Descrizione', type: 'text' },
  { field: 'CAM_Id_Tabelle', label: 'Tabella', type: 'lookup', lookupName: 'tabelleLookup', lookupType: 'select' }, // <<<< CORRECTED FIELD NAME
  { field: 'CAM_Id_TipiCampi', label: 'Tipo Campo', type: 'lookup', lookupName: 'tipiCampiLookup', lookupType: 'select' }, // MODIFIED HERE
];

// Configuration for Lookups - AGGIUNGI IL FALLBACK
const initialLookupConfig = {
  tabelleLookup: { // This is for CAM_Id_Tabelle (parent table)
    tableName: 'SKE_Tabelle',
    valueField: 'TAB_Id_Tabelle',
    labelField: 'TAB_Nome',
  },
  tipiCampiLookup: {
    tableName: 'SKE_TipiCampi',
    valueField: 'TIP_Id_TipiCampi',
    labelField: 'TIP_Descrizione',
  },
  // AGGIUNGI QUESTO: Fallback per CAM_Extra_Tabelle quando non ci sono lookup dinamiche
  extraTabelleByProductLookup: {
    tableName: 'SKE_Tabelle',
    valueField: 'TAB_Id_Tabelle',
    labelField: 'TAB_Nome',
  },
};

// Specific Input Types for Form Fields
const BASE_INPUT_TYPES = {
  CAM_Descrizione: 'textarea',
  CAM_IdRif_Campi: 'number',
  CAM_Sequenza: 'number',
  CAM_Id_Tabelle: 'select', // Handled by lookup
  CAM_Id_TipiCampi: 'select', // Handled by lookup // MODIFIED HERE
  CAM_Extra_Tabelle: 'select', // Handled by lookup
  CAM_ProgressivoOrdinamento: 'number',
  CAM_ListaInFiltro: 'select',
  CAM_InizioValidita: 'datetime-local',
  CAM_FineValidita: 'datetime-local',
  CAM_DataOraInserimento: 'datetime-local',
  CAM_DataOraModifica: 'datetime-local',
  CAM_Timestamp: 'datetime-local',
  // Nuovi campi MILO/HUB
  CAM_ChiaveNaturale: 'boolean',
  CAM_Cercabile: 'boolean',
  CAM_Versioned: 'boolean',
  CAM_Cifrato: 'boolean',
  CAM_NomeUi: 'text',
  CAM_ErroreValidazione: 'text',
};

const hubFieldSet = new Set(hubFields);

const getConfigField = configEntry => String(configEntry).split('=')[0].trim();

const getRecords = response => {
  const records = response?.records || response?.data || response || [];
  return Array.isArray(records) ? records : [];
};

const getSingleFilterValue = value => {
  if (Array.isArray(value)) return value.length === 1 ? value[0] : null;
  return value === '' || value === null || value === undefined ? null : value;
};

const CampiPage = () => {
  const location = useLocation();
  const [activeFilters, setActiveFilters] = useState(null);
  const [parentModuleContext, setParentModuleContext] = useState({
    moduleId: null,
    moduleCode: null,
  });
  const [dynamicLookupOptions, setDynamicLookupOptions] = useState({
    extraTabelleByProduct: [] // For CAM_Extra_Tabelle
  });
  const [isLoadingDynamicLookups, setIsLoadingDynamicLookups] = useState(false);

  // State for Sequence Manager
  const [isSequenceManagerOpen, setIsSequenceManagerOpen] = useState(false);
  const [itemsForSequencer, setItemsForSequencer] = useState([]);
  const [isSequencerLoading, setIsSequencerLoading] = useState(false);
  const updateSequenceRef = useRef(null);

  // AGGIUNTO: State per Ordinamento Modal
  const [isOrdinamentoModalOpen, setIsOrdinamentoModalOpen] = useState(false);
  const [campiForOrdinamento, setCampiForOrdinamento] = useState([]);
  const [selectedTableIdForOrdinamento, setSelectedTableIdForOrdinamento] = useState(null);

  // NUOVO: State per il sub-editor SKE_CampiOperatori
  const [operatoriEditorState, setOperatoriEditorState] = useState({
    isOpen: false,
    campoId: null,
    campoNome: '',
    cercabile: true,
  });

  // NUOVO: Sequenza di default suggerita per il prossimo nuovo campo (calcolata per MILO)
  const [nextSequenzaDominio, setNextSequenzaDominio] = useState(null);

  // This will hold the ID of the SKE_Tabelle record if we are viewing its campi
  const parentTableIdFromState = useMemo(() => {
    // Check both initialFilter_CAM_Id_Tabelle (from other sources) and presetFilter (from RowActionPopup)
    return location.state?.initialFilter_CAM_Id_Tabelle || 
           location.state?.presetFilter?.CAM_Id_Tabelle;
  }, [location.state]);

  // ✅ MODIFICATO: Allineato alla logica di TabellePage
  const [initialFilters, setInitialFilters] = useState({});
  const [tableManagerKey, setTableManagerKey] = useState(Date.now());

  useEffect(() => {
    const filters = {};
    
    // Check for filters from URL parameters (new tab navigation)
    const urlParams = new URLSearchParams(window.location.search);
    const filterKey = urlParams.get('filterKey');
    
    if (filterKey) {
      // ✅ CORRETTO: Controlla sia localStorage che sessionStorage per coerenza
      let filterData = localStorage.getItem(filterKey) || sessionStorage.getItem(filterKey);
      if (filterData) {
        try {
          const parsedData = JSON.parse(filterData);
          if (parsedData.presetFilter?.CAM_Id_Tabelle) {
            filters.CAM_Id_Tabelle = parsedData.presetFilter.CAM_Id_Tabelle;
            logger.info('[CampiPage] Received tableId from new tab navigation:', parsedData.presetFilter.CAM_Id_Tabelle);
          }
          localStorage.removeItem(filterKey);
          sessionStorage.removeItem(filterKey);
        } catch (error) {
          logger.error('[CampiPage] Error parsing filter data from storage:', error);
        }
      }
    } else if (parentTableIdFromState) {
      filters.CAM_Id_Tabelle = parentTableIdFromState;
      logger.info('[CampiPage] Received tableId for filtering CAM_Id_Tabelle:', parentTableIdFromState);
    }

    setInitialFilters(filters);

    // ✅ AGGIUNTO: Forza il re-render di TableManager se ci sono filtri, per garantirne l'applicazione
    if (Object.keys(filters).length > 0) {
      setTableManagerKey(Date.now());
    }

  }, [parentTableIdFromState]);

  const handleFiltersChange = useCallback(filters => {
    setActiveFilters(filters || {});
  }, []);

  const activeParentTableId = useMemo(
    () => getSingleFilterValue(
      (activeFilters === null ? initialFilters : activeFilters).CAM_Id_Tabelle
    ),
    [activeFilters, initialFilters]
  );

  // Risolve sempre il codice del modulo a partire dalla tabella padre. In questo
  // modo l'identificazione HUB non dipende dagli ID, diversi tra ambienti.
  useEffect(() => {
    let cancelled = false;
    setParentModuleContext({ moduleId: null, moduleCode: null });

    if (!activeParentTableId) return () => { cancelled = true; };

    const resolveParentModule = async () => {
      try {
        const tableResponse = await apiService.get('/crud/SKE_Tabelle', {
          params: {
            limit: 1,
            offset: 0,
            fields: 'TAB_Id_Tabelle,TAB_Id_Moduli',
            filters: JSON.stringify({ TAB_Id_Tabelle: activeParentTableId }),
          },
        });
        const table = getRecords(tableResponse).find(
          item => String(item.TAB_Id_Tabelle) === String(activeParentTableId)
        );
        const moduleId = table?.TAB_Id_Moduli;

        if (!moduleId) {
          if (!cancelled) setParentModuleContext({ moduleId: null, moduleCode: null });
          return;
        }

        const moduleResponse = await apiService.get('/crud/SKE_Moduli', {
          params: {
            limit: 1,
            offset: 0,
            fields: 'MOD_Id_Moduli,MOD_CodiceModulo',
            filters: JSON.stringify({ MOD_Id_Moduli: moduleId }),
          },
        });
        const moduleRecord = getRecords(moduleResponse).find(
          item => String(item.MOD_Id_Moduli) === String(moduleId)
        );

        if (!cancelled) {
          setParentModuleContext({
            moduleId,
            moduleCode: moduleRecord?.MOD_CodiceModulo || null,
          });
        }
      } catch (error) {
        logger.warn('[CampiPage] Impossibile risolvere il modulo della tabella padre:', error?.message || error);
        if (!cancelled) setParentModuleContext({ moduleId: null, moduleCode: null });
      }
    };

    resolveParentModule();
    return () => { cancelled = true; };
  }, [activeParentTableId]);

  const parentModuleIdForLookup = parentModuleContext.moduleId;
  const isHub = useMemo(() => isHubModule(parentModuleContext), [parentModuleContext]);

  const columnConfig = useMemo(
    () => isHub
      ? BASE_COLUMN_CONFIG
      : BASE_COLUMN_CONFIG.filter(entry => !hubFieldSet.has(getConfigField(entry))),
    [isHub]
  );

  const inputTypes = useMemo(() => {
    if (isHub) return BASE_INPUT_TYPES;
    return Object.fromEntries(
      Object.entries(BASE_INPUT_TYPES).filter(([field]) => !hubFieldSet.has(field))
    );
  }, [isHub]);

  // Effect to fetch dynamic lookup for CAM_Extra_Tabelle
  useEffect(() => {
    if (parentModuleIdForLookup) {
      const fetchExtraTabelle = async () => {
        setIsLoadingDynamicLookups(true);
        try {
          logger.info(`[CampiPage] Fetching extra tabelle for product using parentModuleId: ${parentModuleIdForLookup}`);
          // Ensure your apiService can make GET requests with query params
          const options = await apiService.get(`/lookup/tables-by-product?moduleId=${parentModuleIdForLookup}`);
          setDynamicLookupOptions(prev => ({ ...prev, extraTabelleByProduct: options || [] }));
          logger.debug('[CampiPage] Fetched extraTabelleByProduct:', options);
        } catch (error) {
          logger.error('[CampiPage] Error fetching extraTabelleByProduct lookup:', error);
          setDynamicLookupOptions(prev => ({ ...prev, extraTabelleByProduct: [] }));
        } finally {
          setIsLoadingDynamicLookups(false);
        }
      };
      fetchExtraTabelle();
    } else {
      // If no parentModuleId (e.g. creating a new CAMPO not linked to a specific table yet, or page opened directly without state)
      // you might want to clear or set default for extraTabelleByProduct
       setDynamicLookupOptions(prev => ({ ...prev, extraTabelleByProduct: [] }));
    }
  }, [parentModuleIdForLookup]);
  
  // Modifica il finalLookupConfig per usare solo le lookup "normali"
  const finalLookupConfig = useMemo(() => {
    const baseConfig = { ...initialLookupConfig };
    
    // Se hai lookup dinamiche, sostituisci con quelle
    if (dynamicLookupOptions.extraTabelleByProduct && dynamicLookupOptions.extraTabelleByProduct.length > 0) {
      baseConfig.extraTabelleByProductLookup = {
        // Usa le opzioni dinamiche pre-caricate invece di fare fetch
        options: dynamicLookupOptions.extraTabelleByProduct,
        isLoading: isLoadingDynamicLookups,
      };
    }
    
    return baseConfig;
  }, [dynamicLookupOptions.extraTabelleByProduct, isLoadingDynamicLookups]);

  const handleOpenSequenceManager = async (fetchAllData, filtersToUse) => {
    if (!fetchAllData) {
      toast.error("Funzione di caricamento non disponibile.");
      return;
    }
    setIsSequencerLoading(true);
    try {
      // Passa i filtri correnti a fetchAllData per assicurarsi che vengano usati
      const records = await fetchAllData(filtersToUse);
      setItemsForSequencer(records || []);
      setIsSequenceManagerOpen(true);
    } catch (error) {
      logger.error("Errore nel caricamento dei campi per l'ordinamento", error);
      toast.error("Impossibile caricare i campi per l'ordinamento.");
    } finally {
      setIsSequencerLoading(false);
    }
  };

  const handleSequenceSave = useCallback(async (updatedItems) => {
    logger.info(`[CampiPage] Attempting to save new sequence for SKE_Campi.`);
    if (updateSequenceRef.current) {
      const success = await updateSequenceRef.current(updatedItems, "CAM_Sequenza", "CAM_Id_Campi");
      if (success) {
        toast.success("Ordinamento salvato con successo!");
        setIsSequenceManagerOpen(false);
      } else {
        toast.error("Errore durante il salvataggio dell'ordinamento.");
      }
    } else {
      logger.error('[CampiPage] updateSequence function ref is not set. Cannot save sequence.');
      toast.error("Funzione di salvataggio non disponibile.");
    }
  }, []);

  // AGGIUNTO: Handler per salvare la configurazione di ordinamento
  const handleOrdinamentoSave = useCallback(async (campiDaAggiornare) => {
    if (!campiDaAggiornare || campiDaAggiornare.length === 0) {
      toast.error('Nessun campo da aggiornare');
      return;
    }

    logger.info('[CampiPage] Salvando configurazione ordinamento', campiDaAggiornare.length, 'campi');
    
    try {
      // Prepara le operazioni di aggiornamento
      const changes = campiDaAggiornare.map(campo => ({
        operation: 'update',
        data: {
          CAM_Id_Campi: campo.CAM_Id_Campi,
          CAM_ProgressivoOrdinamento: campo.CAM_ProgressivoOrdinamento,
          CAM_TipoOrdinamento: campo.CAM_TipoOrdinamento
        }
      }));

      const response = await apiService.post('/crud/SKE_Campi', { changes });
      
      if (response && response.results && response.results.success) {
        toast.success('Configurazione ordinamento salvata con successo!');
        
        // Chiudi la modale
        setIsOrdinamentoModalOpen(false);
        
        // Ricarica i dati (TableManager si aggiornerà automaticamente)
        window.location.reload();
      } else {
        toast.error('Errore durante il salvataggio della configurazione');
      }
    } catch (error) {
      logger.error('Errore durante il salvataggio ordinamento:', error);
      toast.error(`Errore: ${error.message}`);
    }
  }, [selectedTableIdForOrdinamento]);

  // ✅ CORRETTO: Logica per la ricerca automatica allineata a TabellePage
  const autoSearchOnInitialFilters = useMemo(() => {
    return Object.keys(initialFilters).length > 0;
  }, [initialFilters]);

  const shouldAutoSearchOnLoad = useMemo(() => {
    return Object.keys(initialFilters).length === 0;
  }, [initialFilters]);

  // NUOVO: Quando abbiamo la tabella padre + il modulo, calcola la prossima CAM_Sequenza
  // di dominio (max(CAM_Sequenza WHERE CAM_Sequenza <= 1000) + 10).
  useEffect(() => {
    const tableId = activeParentTableId;
    if (!isHub || !tableId) {
      setNextSequenzaDominio(null);
      return;
    }
    let cancelled = false;
    const fetchMaxSequenza = async () => {
      try {
        const params = {
          limit: 9999,
          offset: 0,
          filters: JSON.stringify({ CAM_Id_Tabelle: tableId }),
        };
        const response = await apiService.get('/crud/SKE_Campi', { params });
        const records = response?.records || response?.data || response || [];
        if (!Array.isArray(records)) {
          setNextSequenzaDominio(10);
          return;
        }
        const max = records.reduce((m, r) => {
          const seq = Number(r.CAM_Sequenza) || 0;
          return seq > 0 && seq <= 1000 && seq > m ? seq : m;
        }, 0);
        if (!cancelled) setNextSequenzaDominio(max + 10);
      } catch (err) {
        logger.warn('[CampiPage] Impossibile calcolare next CAM_Sequenza:', err?.message);
        if (!cancelled) setNextSequenzaDominio(null);
      }
    };
    fetchMaxSequenza();
    return () => { cancelled = true; };
  }, [activeParentTableId, isHub]);

  // ✅ NUOVO: Calcola i valori di default per la creazione dai filtri attivi  
  const defaultValuesForCreate = useMemo(() => {
    const defaults = {};
    
    // Se c'è un filtro per CAM_Id_Tabelle, usalo come default per i nuovi record
    if (activeParentTableId) {
      defaults.CAM_Id_Tabelle = activeParentTableId;
    }

    // NUOVO: Default modulo-specifici per i nuovi campi MILO/HUB
    if (isHub) {
      defaults.CAM_ChiaveNaturale = false;
      defaults.CAM_Cercabile = false;
      defaults.CAM_Versioned = true; // Coerente con CAM_Versioned DEFAULT 1 dello schema Milo
      defaults.CAM_Cifrato = false;
      defaults.CAM_NomeUi = '';
      defaults.CAM_ErroreValidazione = '';
    }

    // Per il modulo HUB, suggerisci la prossima sequenza di dominio (<=1000)
    if (isHub && nextSequenzaDominio != null) {
      defaults.CAM_Sequenza = nextSequenzaDominio;
    }
    
    return defaults;
  }, [activeParentTableId, isHub, nextSequenzaDominio]);

  // NUOVO: Apertura sub-editor operatori per il campo selezionato
  const handleOpenOperatoriEditor = useCallback((selectedRow) => {
    if (!isHub) return;
    if (!selectedRow || !selectedRow.CAM_Id_Campi || selectedRow.CAM_Id_Campi < 0) {
      toast.warning('Salva prima il campo per gestirne gli operatori di ricerca');
      return;
    }
    setOperatoriEditorState({
      isOpen: true,
      campoId: selectedRow.CAM_Id_Campi,
      campoNome: selectedRow.CAM_Nome || '',
      cercabile: !!selectedRow.CAM_Cercabile,
    });
  }, [isHub]);

  useEffect(() => {
    if (!isHub && operatoriEditorState.isOpen) {
      setOperatoriEditorState(state => ({ ...state, isOpen: false }));
    }
  }, [isHub, operatoriEditorState.isOpen]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
        Gestione Campi
      </h1>

      <TableManager
        key={`campi-manager-${tableManagerKey}`} // ✅ AGGIUNTO: Key per forzare il re-mount
        tableName="SKE_Campi"
        primaryKey="CAM_Id_Campi"
        pageTitle="Gestione Campi"
        columnConfig={columnConfig}
        filterConfig={filterConfig}
        lookupConfig={finalLookupConfig}
        inputTypes={inputTypes}
        enableCreate={true}
        enableEdit={true}
        enableDelete={true}
        autoSearchOnLoad={shouldAutoSearchOnLoad}  // ✅ CORRETTO: Caricamento condizionale
        defaultSortField="CAM_Sequenza"
        defaultSortDirection="asc"
        enableSmoothScrolling={true}
        initialFilters={initialFilters}
        autoSearchOnInitialFilters={autoSearchOnInitialFilters} // ✅ CORRETTO: Ricerca automatica con filtri
        defaultValuesForCreate={defaultValuesForCreate} // ✅ AGGIUNTO: Passa i valori di default
        onFiltersChange={handleFiltersChange}
        renderCustomFormActions={(selectedRow) => (
          <>
            {isHub && (
              <button
                type="button"
                onClick={() => handleOpenOperatoriEditor(selectedRow)}
                className={`px-4 py-2 text-sm border rounded-md transition flex items-center justify-center ${
                  selectedRow?.CAM_Cercabile
                    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 dark:border-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                    : 'border-yellow-500 text-yellow-600 dark:text-yellow-400 dark:border-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30'
                }`}
                title={selectedRow?.CAM_Cercabile
                  ? 'Gestisci operatori di ricerca per questo campo'
                  : 'Campo non cercabile: gli operatori esistenti non saranno emessi nei template'}
              >
                Operatori ricerca
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
              if (!selectedRow || !selectedRow.CAM_Id_Tabelle) {
                toast.error('ID Tabella non trovato');
                return;
              }
              try {
                logger.info('[CampiPage] Loading campi for table:', selectedRow.CAM_Id_Tabelle);
                
                // Usa lo stesso formato di chiamata di TableManager con filtri JSON
                const filters = { CAM_Id_Tabelle: selectedRow.CAM_Id_Tabelle };
                const params = {
                  limit: 9999,
                  offset: 0,
                  filters: JSON.stringify(filters)
                };
                
                logger.debug('[CampiPage] API Request params:', params);
                
                const response = await apiService.get('/crud/SKE_Campi', { params });
                
                logger.debug('[CampiPage] API Response:', response);
                
                // La risposta dovrebbe avere records o data
                const campiData = response?.records || response?.data || response || [];
                
                logger.info('[CampiPage] Loaded campi:', Array.isArray(campiData) ? campiData.length : 'not an array');
                
                if (!Array.isArray(campiData) || campiData.length === 0) {
                  toast.warning('Nessun campo trovato per questa tabella');
                  return;
                }
                
                setCampiForOrdinamento(campiData);
                setSelectedTableIdForOrdinamento(selectedRow.CAM_Id_Tabelle);
                setIsOrdinamentoModalOpen(true);
              } catch (error) {
                logger.error('[CampiPage] Error loading campi for ordinamento:', error);
                toast.error('Errore durante il caricamento dei campi: ' + (error.message || 'Errore sconosciuto'));
              }
            }}
            className="px-4 py-2 text-sm border border-purple-500 text-purple-600 dark:text-purple-400 dark:border-purple-600 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/30 transition flex items-center justify-center"
          >
            <FaSortAmountDown className="mr-1.5 h-4 w-4" />
            Orderby default
          </button>
          </>
        )}
      />

      {isSequenceManagerOpen && (
        <DragDropSequenceManager
          isOpen={isSequenceManagerOpen}
          onClose={() => setIsSequenceManagerOpen(false)}
          items={itemsForSequencer}
          onSave={handleSequenceSave}
          idField="CAM_Id_Campi"
          sequenceField="CAM_Sequenza"
          displayField="CAM_Nome"
          descriptionField="CAM_Descrizione"
          title="Ordinamento Campi della Tabella"
        />
      )}

      {/* AGGIUNTO: Modal per configurare ordinamento default */}
      {isOrdinamentoModalOpen && (
        <OrdinamentoCampiModal
          isOpen={isOrdinamentoModalOpen}
          onClose={() => setIsOrdinamentoModalOpen(false)}
          campi={campiForOrdinamento}
          onSave={handleOrdinamentoSave}
          themeColor="purple"
        />
      )}

      {/* NUOVO: Sub-editor SKE_CampiOperatori */}
      {isHub && (
        <CampiOperatoriEditor
          isOpen={operatoriEditorState.isOpen}
          onClose={() => setOperatoriEditorState(s => ({ ...s, isOpen: false }))}
          campoId={operatoriEditorState.campoId}
          campoNome={operatoriEditorState.campoNome}
          cercabile={operatoriEditorState.cercabile}
          themeColor="emerald"
        />
      )}
    </div>
  );
};

export default CampiPage;
